<template>
    <div class="navigator-page">
        <!-- Sidebar: conversation history -->
        <aside class="navigator-sidebar" :class="{ 'navigator-sidebar--open': sidebarOpen }">
            <div class="navigator-sidebar__header">
                <div class="navigator-sidebar__brand">
                    <span class="navigator-sidebar__compass">&#x1F9ED;</span>
                    Navigator
                </div>
                <button class="navigator-sidebar__new" @click="newConversation" title="New conversation">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
                </button>
            </div>

            <div class="navigator-sidebar__list">
                <button v-for="conv in savedConversations" :key="conv.id"
                    class="navigator-sidebar__item"
                    :class="{ 'navigator-sidebar__item--active': conv.id === conversationId }"
                    @click="loadConversation(conv.id)">
                    <div class="navigator-sidebar__item-title">{{ conv.title }}</div>
                    <div class="navigator-sidebar__item-date">{{ formatDate(conv.date) }}</div>
                </button>
                <div v-if="savedConversations.length === 0" class="navigator-sidebar__empty">
                    No conversations yet
                </div>
            </div>
        </aside>

        <!-- Main chat area -->
        <main class="navigator-main">
            <!-- Mobile header with sidebar toggle -->
            <div class="navigator-topbar">
                <button class="navigator-topbar__toggle" @click="sidebarOpen = !sidebarOpen">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M3 12h18M3 6h18M3 18h18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
                </button>
                <div class="navigator-topbar__title">
                    <span class="navigator-topbar__compass">&#x1F9ED;</span>
                    Navigator
                </div>
                <button class="navigator-topbar__new" @click="newConversation" title="New conversation">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
                </button>
            </div>

            <!-- Messages area -->
            <div class="navigator-messages" ref="messagesContainer">
                <!-- Welcome state -->
                <div v-if="messages.length === 0" class="navigator-welcome">
                    <div class="navigator-welcome__icon">&#x1F9ED;</div>
                    <h1 class="navigator-welcome__title">Solana Compass Navigator</h1>
                    <p class="navigator-welcome__subtitle">
                        Ask anything about Solana &mdash; token prices, network stats, DEX analytics, validator performance, and more.
                    </p>
                    <div class="navigator-welcome__grid">
                        <button v-for="suggestion in defaultSuggestions" :key="suggestion.text"
                            class="navigator-welcome__card"
                            @click="sendMessage(suggestion.text)">
                            <div class="navigator-welcome__card-icon">{{ suggestion.icon }}</div>
                            <div class="navigator-welcome__card-text">{{ suggestion.text }}</div>
                        </button>
                    </div>
                </div>

                <!-- Message list -->
                <div v-for="(msg, index) in messages" :key="index"
                    class="navigator-msg"
                    :class="'navigator-msg--' + msg.role">
                    <div class="navigator-msg__inner">
                        <!-- User message -->
                        <div v-if="msg.role === 'user'" class="navigator-msg__user">
                            {{ msg.text }}
                        </div>

                        <!-- Assistant message with parsed segments -->
                        <div v-else class="navigator-msg__assistant">
                            <template v-for="(segment, sIdx) in msg.segments" :key="sIdx">
                                <div v-if="segment.type === 'markdown'" v-html="renderMarkdown(segment.content)" class="navigator-prose"></div>
                                <navigator-metric-cards v-else-if="segment.type === 'metric-cards'" :cards="segment.props.cards" />
                                <navigator-chart v-else-if="segment.type === 'chart'" v-bind="segment.props" />
                                <navigator-data-table v-else-if="segment.type === 'comparison-table' || segment.type === 'data-table'"
                                    :title="segment.props.title"
                                    :content="segment.props.content"
                                    :sortable="segment.props.sortable" />
                                <navigator-callout v-else-if="segment.type === 'callout'"
                                    :type="segment.props.type"
                                    :content="segment.props.content" />
                                <navigator-suggestions v-else-if="segment.type === 'suggestions'"
                                    :items="segment.props.items"
                                    @select="sendMessage" />
                                <navigator-token-badge v-else-if="segment.type === 'token'" v-bind="segment.props" />
                            </template>

                            <!-- Tool calls indicator -->
                            <div v-if="msg.toolCalls && msg.toolCalls.length > 0" class="navigator-msg__tools">
                                <span class="navigator-msg__tools-label">Data sources:</span>
                                <span v-for="tool in msg.toolCalls" :key="tool.name" class="navigator-msg__tools-badge">
                                    {{ formatToolName(tool.name) }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Loading indicator -->
                <div v-if="isLoading" class="navigator-msg navigator-msg--assistant">
                    <div class="navigator-msg__inner">
                        <div class="navigator-loading">
                            <div class="navigator-loading__dots">
                                <span></span><span></span><span></span>
                            </div>
                            <span v-if="loadingStatus" class="navigator-loading__status">{{ loadingStatus }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Input area -->
            <div class="navigator-input">
                <div class="navigator-input__container">
                    <div class="navigator-input__wrapper">
                        <textarea
                            ref="inputField"
                            v-model="inputText"
                            @keydown.enter.exact.prevent="sendMessage(inputText)"
                            placeholder="Ask about Solana..."
                            rows="1"
                            :disabled="isLoading"
                            class="navigator-input__field"
                        ></textarea>
                        <button
                            class="navigator-input__send"
                            @click="sendMessage(inputText)"
                            :disabled="!inputText.trim() || isLoading">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </button>
                    </div>
                    <div class="navigator-input__footer">
                        Powered by AI. Data from Solana Compass analytics. Results may be incomplete.
                    </div>
                </div>
            </div>
        </main>
    </div>
</template>

<script>
import { ref, nextTick, onMounted, computed } from 'vue';
import { useDirectiveParser } from '../composables/useDirectiveParser';

const STORAGE_KEY = 'navigator_conversations';

export default {
    name: 'NavigatorChat',
    setup() {
        const { parse } = useDirectiveParser();

        const messages = ref([]);
        const inputText = ref('');
        const isLoading = ref(false);
        const loadingStatus = ref('');
        const conversationId = ref(null);
        const messagesContainer = ref(null);
        const inputField = ref(null);
        const sidebarOpen = ref(false);

        const defaultSuggestions = [
            { icon: '📊', text: 'How is Solana performing today?' },
            { icon: '🔄', text: 'What are the top DEXs by volume?' },
            { icon: '🪙', text: 'Tell me about Jupiter token' },
            { icon: '💰', text: 'Show me network fee trends' },
            { icon: '🏆', text: 'Top validators by DeFi volume' },
            { icon: '📈', text: 'Compare SOL vs ETH price' },
        ];

        // Load saved conversations from localStorage
        const savedConversations = computed(() => {
            try {
                const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
                return data.sort((a, b) => new Date(b.date) - new Date(a.date));
            } catch {
                return [];
            }
        });

        function saveToStorage() {
            if (!conversationId.value || messages.value.length === 0) return;

            try {
                const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
                const firstUserMsg = messages.value.find(m => m.role === 'user');
                const title = firstUserMsg
                    ? firstUserMsg.text.substring(0, 60) + (firstUserMsg.text.length > 60 ? '...' : '')
                    : 'New conversation';

                const existing = stored.findIndex(c => c.id === conversationId.value);
                const entry = {
                    id: conversationId.value,
                    title,
                    date: new Date().toISOString(),
                    messages: messages.value.map(m => ({
                        role: m.role,
                        text: m.text,
                        segments: m.segments,
                        toolCalls: m.toolCalls,
                    })),
                };

                if (existing >= 0) {
                    stored[existing] = entry;
                } else {
                    stored.unshift(entry);
                }

                // Keep max 50 conversations
                if (stored.length > 50) stored.length = 50;

                localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
            } catch (e) {
                console.warn('Failed to save conversation:', e);
            }
        }

        function loadConversation(id) {
            try {
                const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
                const conv = stored.find(c => c.id === id);
                if (conv) {
                    conversationId.value = id;
                    messages.value = conv.messages || [];
                    sidebarOpen.value = false;
                    scrollToBottom();
                }
            } catch (e) {
                console.warn('Failed to load conversation:', e);
            }
        }

        function newConversation() {
            messages.value = [];
            conversationId.value = null;
            loadingStatus.value = '';
            sidebarOpen.value = false;
            nextTick(() => inputField.value?.focus());
        }

        async function sendMessage(text) {
            if (typeof text !== 'string' || !text.trim() || isLoading.value) return;

            const userMessage = text.trim();
            inputText.value = '';
            messages.value.push({ role: 'user', text: userMessage });
            isLoading.value = true;
            loadingStatus.value = 'Thinking...';

            scrollToBottom();

            // Cycle through status messages while waiting
            const statusMessages = ['Thinking...', 'Querying data...', 'Analysing results...'];
            let statusIdx = 0;
            const statusInterval = setInterval(() => {
                statusIdx = (statusIdx + 1) % statusMessages.length;
                loadingStatus.value = statusMessages[statusIdx];
            }, 3000);

            try {
                const response = await fetch('/api/v1/navigator/chat-sync', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content || '',
                    },
                    body: JSON.stringify({
                        message: userMessage,
                        conversation_id: conversationId.value,
                    }),
                });

                if (!response.ok) {
                    const err = await response.json().catch(() => ({}));
                    throw new Error(err.error || `HTTP ${response.status}`);
                }

                const result = await response.json();
                const data = result.data;

                if (data.conversation_id) {
                    conversationId.value = data.conversation_id;
                }

                const fullText = data.message || '';
                const segments = parse(fullText);
                messages.value.push({
                    role: 'assistant',
                    text: fullText,
                    segments,
                    toolCalls: data.tool_calls || [],
                });

            } catch (error) {
                messages.value.push({
                    role: 'assistant',
                    text: error.message,
                    segments: [{
                        type: 'callout',
                        props: { type: 'danger', content: error.message },
                    }],
                    toolCalls: [],
                });
            } finally {
                clearInterval(statusInterval);
                isLoading.value = false;
                loadingStatus.value = '';
                saveToStorage();
                scrollToBottom();
            }
        }

        function renderMarkdown(text) {
            if (!text) return '';
            return text
                .replace(/^### (.+)$/gm, '<h4>$1</h4>')
                .replace(/^## (.+)$/gm, '<h3>$1</h3>')
                .replace(/^# (.+)$/gm, '<h2>$1</h2>')
                .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                .replace(/\*(.+?)\*/g, '<em>$1</em>')
                .replace(/`([^`]+)`/g, '<code>$1</code>')
                .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="navigator-link">$1</a>')
                .replace(/\n\n/g, '</p><p>')
                .replace(/\n/g, '<br>')
                .replace(/^(.+)$/s, '<p>$1</p>');
        }

        function formatToolName(name) {
            return name
                .replace(/^get_/, '')
                .replace(/_/g, ' ')
                .replace(/\b\w/g, c => c.toUpperCase());
        }

        function formatDate(dateStr) {
            const d = new Date(dateStr);
            const now = new Date();
            const diffMs = now - d;
            const diffMins = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMs / 3600000);
            const diffDays = Math.floor(diffMs / 86400000);

            if (diffMins < 1) return 'Just now';
            if (diffMins < 60) return `${diffMins}m ago`;
            if (diffHours < 24) return `${diffHours}h ago`;
            if (diffDays < 7) return `${diffDays}d ago`;
            return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        }

        function scrollToBottom() {
            nextTick(() => {
                if (messagesContainer.value) {
                    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
                }
            });
        }

        onMounted(() => {
            nextTick(() => inputField.value?.focus());
        });

        return {
            messages,
            inputText,
            isLoading,
            loadingStatus,
            conversationId,
            defaultSuggestions,
            savedConversations,
            sidebarOpen,
            messagesContainer,
            inputField,
            newConversation,
            loadConversation,
            sendMessage,
            renderMarkdown,
            formatToolName,
            formatDate,
        };
    },
};
</script>

<style>
/* ============================================================
   Navigator — Theme tokens (unscoped so child components inherit)
   ============================================================ */

/* Light mode defaults */
.navigator-page {
    --nav-bg: #f8f9fa;
    --nav-sidebar-bg: #ffffff;
    --nav-sidebar-border: #e5e7eb;
    --nav-msg-bg: #ffffff;
    --nav-user-bg: rgba(139, 92, 246, 0.08);
    --nav-user-border: rgba(139, 92, 246, 0.15);
    --nav-text: #1f2937;
    --nav-text-secondary: #6b7280;
    --nav-text-muted: #9ca3af;
    --nav-border: #e5e7eb;
    --nav-border-subtle: #f3f4f6;
    --nav-input-bg: #ffffff;
    --nav-input-border: #d1d5db;
    --nav-hover: rgba(139, 92, 246, 0.06);
    --nav-code-bg: rgba(139, 92, 246, 0.06);
    --nav-code-text: #7c3aed;
    --nav-card-bg: rgba(139, 92, 246, 0.04);
    --nav-card-border: rgba(139, 92, 246, 0.12);
    --nav-accent: #8b5cf6;
    --nav-accent-soft: rgba(139, 92, 246, 0.1);
    --nav-table-header-bg: #f9fafb;
    --nav-table-border: #f3f4f6;
    --nav-chart-bg: #f9fafb;
    --nav-scrollbar: rgba(139, 92, 246, 0.2);
    --nav-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

/* Dark mode overrides */
body.dark-mode .navigator-page {
    --nav-bg: #0f0f14;
    --nav-sidebar-bg: #0a0a0f;
    --nav-sidebar-border: rgba(255, 255, 255, 0.06);
    --nav-msg-bg: transparent;
    --nav-user-bg: rgba(139, 92, 246, 0.12);
    --nav-user-border: rgba(139, 92, 246, 0.2);
    --nav-text: #e5e7eb;
    --nav-text-secondary: #9ca3af;
    --nav-text-muted: #6b7280;
    --nav-border: rgba(255, 255, 255, 0.06);
    --nav-border-subtle: rgba(255, 255, 255, 0.03);
    --nav-input-bg: rgba(255, 255, 255, 0.04);
    --nav-input-border: rgba(255, 255, 255, 0.1);
    --nav-hover: rgba(139, 92, 246, 0.1);
    --nav-code-bg: rgba(139, 92, 246, 0.1);
    --nav-code-text: #c4b5fd;
    --nav-card-bg: rgba(139, 92, 246, 0.06);
    --nav-card-border: rgba(139, 92, 246, 0.15);
    --nav-accent: #8b5cf6;
    --nav-accent-soft: rgba(139, 92, 246, 0.15);
    --nav-table-header-bg: rgba(0, 0, 0, 0.3);
    --nav-table-border: rgba(255, 255, 255, 0.06);
    --nav-chart-bg: rgba(0, 0, 0, 0.2);
    --nav-scrollbar: rgba(139, 92, 246, 0.3);
    --nav-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}
</style>

<style scoped>

/* Layout — fills the fixed-position wrapper from the blade template */
.navigator-page {
    display: flex;
    height: 100%;
    background: var(--nav-bg);
    font-family: 'Nunito', sans-serif;
    overflow: hidden;
}

/* ---- Sidebar ---- */
.navigator-sidebar {
    width: 260px;
    flex-shrink: 0;
    background: var(--nav-sidebar-bg);
    border-right: 1px solid var(--nav-sidebar-border);
    display: flex;
    flex-direction: column;
}

.navigator-sidebar__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    border-bottom: 1px solid var(--nav-border);
}

.navigator-sidebar__brand {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    font-weight: 700;
    color: var(--nav-text);
}

.navigator-sidebar__compass {
    font-size: 20px;
}

.navigator-sidebar__new {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: var(--nav-accent-soft);
    border: 1px solid var(--nav-card-border);
    color: var(--nav-accent);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s;
}

.navigator-sidebar__new:hover {
    background: var(--nav-hover);
    border-color: var(--nav-accent);
}

.navigator-sidebar__list {
    flex: 1;
    overflow-y: auto;
    padding: 8px;
}

.navigator-sidebar__list::-webkit-scrollbar { width: 4px; }
.navigator-sidebar__list::-webkit-scrollbar-thumb { background: var(--nav-scrollbar); border-radius: 2px; }

.navigator-sidebar__item {
    display: block;
    width: 100%;
    text-align: left;
    background: none;
    border: 1px solid transparent;
    border-radius: 8px;
    padding: 10px 12px;
    cursor: pointer;
    transition: all 0.15s;
    margin-bottom: 2px;
}

.navigator-sidebar__item:hover {
    background: var(--nav-hover);
}

.navigator-sidebar__item--active {
    background: var(--nav-accent-soft);
    border-color: var(--nav-card-border);
}

.navigator-sidebar__item-title {
    font-size: 13px;
    font-weight: 500;
    color: var(--nav-text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.navigator-sidebar__item-date {
    font-size: 11px;
    color: var(--nav-text-muted);
    margin-top: 2px;
}

.navigator-sidebar__empty {
    text-align: center;
    color: var(--nav-text-muted);
    font-size: 13px;
    padding: 32px 16px;
}

/* ---- Main area ---- */
.navigator-main {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
}

/* Mobile topbar (hidden on desktop) */
.navigator-topbar {
    display: none;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    border-bottom: 1px solid var(--nav-border);
    background: var(--nav-sidebar-bg);
}

.navigator-topbar__toggle,
.navigator-topbar__new {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: none;
    border: 1px solid var(--nav-border);
    color: var(--nav-text-secondary);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
}

.navigator-topbar__title {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 700;
    font-size: 16px;
    color: var(--nav-text);
}

.navigator-topbar__compass { font-size: 20px; }

/* ---- Messages ---- */
.navigator-messages {
    flex: 1;
    overflow-y: auto;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.navigator-messages::-webkit-scrollbar { width: 6px; }
.navigator-messages::-webkit-scrollbar-thumb { background: var(--nav-scrollbar); border-radius: 3px; }

/* Welcome state */
.navigator-welcome {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    text-align: center;
    padding: 40px 24px;
    max-width: 720px;
    margin: 0 auto;
}

.navigator-welcome__icon {
    font-size: 48px;
    margin-bottom: 16px;
}

.navigator-welcome__title {
    font-size: 28px;
    font-weight: 700;
    color: var(--nav-text);
    margin: 0 0 8px;
}

.navigator-welcome__subtitle {
    font-size: 16px;
    color: var(--nav-text-secondary);
    margin: 0 0 32px;
    max-width: 480px;
    line-height: 1.5;
}

.navigator-welcome__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 12px;
    width: 100%;
    max-width: 640px;
}

.navigator-welcome__card {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 16px;
    background: var(--nav-card-bg);
    border: 1px solid var(--nav-card-border);
    border-radius: var(--compass-radius-sm, 8px);
    cursor: pointer;
    transition: all 0.15s;
    text-align: left;
    color: var(--nav-text);
    font-size: 14px;
    line-height: 1.4;
}

.navigator-welcome__card:hover {
    border-color: var(--nav-accent);
    background: var(--nav-hover);
    box-shadow: var(--nav-shadow);
}

.navigator-welcome__card-icon {
    font-size: 20px;
    flex-shrink: 0;
}

.navigator-welcome__card-text {
    font-weight: 500;
}

/* ---- Message bubbles ---- */
.navigator-msg {
    display: flex;
}

.navigator-msg__inner {
    max-width: 840px;
    width: 100%;
}

.navigator-msg--user {
    justify-content: flex-end;
}

.navigator-msg--user .navigator-msg__inner {
    max-width: 600px;
}

.navigator-msg__user {
    background: var(--nav-user-bg);
    border: 1px solid var(--nav-user-border);
    border-radius: 16px 16px 4px 16px;
    padding: 12px 18px;
    color: var(--nav-text);
    font-size: 14px;
    line-height: 1.5;
}

.navigator-msg__assistant {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

/* Markdown prose */
.navigator-prose {
    color: var(--nav-text);
    font-size: 14px;
    line-height: 1.7;
}

.navigator-prose :deep(h2) {
    font-size: 20px;
    font-weight: 700;
    color: var(--nav-text);
    margin: 0 0 8px;
}

.navigator-prose :deep(h3) {
    font-size: 17px;
    font-weight: 600;
    color: var(--nav-text);
    margin: 0 0 6px;
}

.navigator-prose :deep(h4) {
    font-size: 15px;
    font-weight: 600;
    color: var(--nav-text);
    margin: 0 0 4px;
}

.navigator-prose :deep(p) {
    margin: 0 0 12px;
}

.navigator-prose :deep(p:last-child) {
    margin-bottom: 0;
}

.navigator-prose :deep(strong) {
    color: var(--nav-text);
    font-weight: 700;
}

.navigator-prose :deep(code) {
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    background: var(--nav-code-bg);
    border: 1px solid var(--nav-card-border);
    padding: 2px 6px;
    border-radius: 4px;
    color: var(--nav-code-text);
}

.navigator-prose :deep(a.navigator-link) {
    color: var(--nav-accent);
    text-decoration: none;
}

.navigator-prose :deep(a.navigator-link:hover) {
    text-decoration: underline;
}

/* Tool calls badge */
.navigator-msg__tools {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    padding-top: 12px;
    border-top: 1px solid var(--nav-border-subtle);
}

.navigator-msg__tools-label {
    font-size: 11px;
    color: var(--nav-text-muted);
}

.navigator-msg__tools-badge {
    font-size: 11px;
    font-family: 'JetBrains Mono', monospace;
    color: var(--nav-text-secondary);
    background: var(--nav-card-bg);
    border: 1px solid var(--nav-card-border);
    padding: 2px 8px;
    border-radius: 4px;
}

/* Loading indicator */
.navigator-loading {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 0;
}

.navigator-loading__dots {
    display: flex;
    gap: 4px;
}

.navigator-loading__dots span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--nav-accent);
    animation: navigatorPulse 1.4s ease-in-out infinite;
}

.navigator-loading__dots span:nth-child(2) { animation-delay: 0.2s; }
.navigator-loading__dots span:nth-child(3) { animation-delay: 0.4s; }

@keyframes navigatorPulse {
    0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
    40% { opacity: 1; transform: scale(1); }
}

.navigator-loading__status {
    font-size: 12px;
    color: var(--nav-text-muted);
}

/* ---- Input area ---- */
.navigator-input {
    padding: 16px 24px 20px;
    border-top: 1px solid var(--nav-border);
    background: var(--nav-sidebar-bg);
}

.navigator-input__container {
    max-width: 840px;
    margin: 0 auto;
}

.navigator-input__wrapper {
    display: flex;
    align-items: flex-end;
    background: var(--nav-input-bg);
    border: 1px solid var(--nav-input-border);
    border-radius: 12px;
    padding: 10px 10px 10px 18px;
    transition: border-color 0.2s;
    box-shadow: var(--nav-shadow);
}

.navigator-input__wrapper:focus-within {
    border-color: var(--nav-accent);
}

.navigator-input__field {
    flex: 1;
    background: none;
    border: none;
    color: var(--nav-text);
    font-size: 15px;
    font-family: 'Nunito', sans-serif;
    resize: none;
    max-height: 150px;
    outline: none;
    line-height: 1.5;
}

.navigator-input__field::placeholder {
    color: var(--nav-text-muted);
}

.navigator-input__send {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    background: linear-gradient(135deg, #8b5cf6, #ec4899);
    border: none;
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: opacity 0.15s;
}

.navigator-input__send:disabled {
    opacity: 0.3;
    cursor: not-allowed;
}

.navigator-input__send:not(:disabled):hover {
    opacity: 0.9;
}

.navigator-input__footer {
    font-size: 11px;
    color: var(--nav-text-muted);
    text-align: center;
    margin-top: 8px;
}

/* ---- Responsive ---- */
@media (max-width: 768px) {
    .navigator-sidebar {
        position: fixed;
        top: 0;
        left: 0;
        bottom: 0;
        z-index: 100;
        transform: translateX(-100%);
        transition: transform 0.25s ease;
        box-shadow: var(--nav-shadow);
    }

    .navigator-sidebar--open {
        transform: translateX(0);
    }

    .navigator-topbar {
        display: flex;
    }

    .navigator-page {
        height: 100%;
    }

    .navigator-messages {
        padding: 16px;
    }

    .navigator-input {
        padding: 12px 16px 16px;
    }

    .navigator-welcome__grid {
        grid-template-columns: 1fr;
    }

    .navigator-welcome__title {
        font-size: 22px;
    }
}
</style>
